/**
 * Phase 9 Slice 5 — Experiments UI (list + detail + minimal create).
 *
 * Scope deliberately minimal to stay inside Slice 9.5 budget:
 *   • List view with status, primary metric, traffic split, winner.
 *   • Detail view with per-variant cards (exposures, conversions,
 *     CR, Wilson CI, p-value, winner badge).
 *   • Minimal create form (no slider auto-balance wizard — deferred
 *     to `experiments-create-wizard` follow-up).
 *   • Pause/Resume/Complete actions, RBAC-gated.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, auth } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../../components/ui/select";

const WRITE_ROLES = new Set(["owner", "admin", "manager"]);

function StatusBadge({ status }) {
  const tone = status === "running" ? "bg-emerald-100 text-emerald-900"
    : status === "completed" ? "bg-blue-100 text-blue-900"
    : status === "paused" ? "bg-amber-100 text-amber-900"
    : "bg-stone-100 text-stone-900";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
            data-testid={`experiment-status-${status}`}>{status}</span>
  );
}

function WinnerBadge({ winner }) {
  if (!winner) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-900"
            data-testid="experiment-winner-badge">
      Winner: {winner.slice(0, 12)}
    </span>
  );
}

function pct(v) { return ((v ?? 0) * 100).toFixed(1) + "%"; }

// Phase 9.5.1 — prefer the canonical field names from the backend
// (`conversion_rate`, `conversion_rate_ci.{low,high}`) and fall back to
// the deprecated flat aliases (`primary_rate`, `ci_low`, `ci_high`)
// so the UI keeps working against a not-yet-deployed backend.
function rateOf(v) {
  return v?.conversion_rate ?? v?.primary_rate ?? 0;
}
function ciOf(v) {
  if (v?.conversion_rate_ci) return v.conversion_rate_ci;
  return { low: v?.ci_low ?? 0, high: v?.ci_high ?? 0 };
}

export function ExperimentsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = auth.getUser ? auth.getUser() : { role: "owner" };
  const canWrite = WRITE_ROLES.has(user?.role);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/experiments");
        if (alive) setItems(r.data.items || []);
      } catch (e) { toast.error("Failed to load experiments"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-6" data-testid="experiments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Experiments</h1>
          <p className="text-sm text-muted-foreground">
            A/B test landing pages and ad creatives.
          </p>
        </div>
        {canWrite && (
          <Button data-testid="experiment-new-btn"
                  onClick={() => navigate("/app/experiments/new")}>
            New experiment
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground"
                                data-testid="experiments-empty">
          No experiments yet.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {items.map(ex => (
            <Card key={ex.id} data-testid={`experiment-row-${ex.id}`}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/app/experiments/${ex.id}`)}>
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium truncate">{ex.name}</span>
                    <StatusBadge status={ex.status} />
                    <Badge variant="outline" className="text-[11px]">
                      {ex.subject_type}
                    </Badge>
                    <Badge variant="outline" className="text-[11px]">
                      {ex.primary_metric}
                    </Badge>
                    <WinnerBadge winner={ex.winner_subject_id} />
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Split: {(ex.traffic_split || []).map(s => pct(s)).join(" / ")}
                    · Subjects: {ex.subject_ids?.length || 0}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function ExperimentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const user = auth.getUser ? auth.getUser() : { role: "owner" };
  const canWrite = WRITE_ROLES.has(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/experiments/${id}`);
      setData(r.data);
    } catch (e) { toast.error("Failed to load experiment"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const act = async (verb) => {
    setActing(true);
    try {
      await api.post(`/experiments/${id}/${verb}`);
      toast.success(`Experiment ${verb}d`);
      await load();
    } catch (e) {
      toast.error(`Failed to ${verb}`);
    } finally { setActing(false); }
  };

  if (loading || !data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const r = data.results || { variants: [] };

  return (
    <div className="space-y-6" data-testid="experiment-detail">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{data.name}</h1>
          <div className="flex items-center gap-2 mt-1">
            <StatusBadge status={data.status} />
            <Badge variant="outline">{data.subject_type}</Badge>
            <Badge variant="outline">{data.primary_metric}</Badge>
            <WinnerBadge winner={data.winner_subject_id} />
          </div>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            {data.status === "running" && (
              <Button variant="outline" disabled={acting}
                      data-testid="experiment-pause-btn"
                      onClick={() => act("pause")}>Pause</Button>
            )}
            {data.status === "paused" && (
              <Button variant="outline" disabled={acting}
                      data-testid="experiment-resume-btn"
                      onClick={() => act("resume")}>Resume</Button>
            )}
            {(data.status === "running" || data.status === "paused") && (
              <Button disabled={acting}
                      data-testid="experiment-complete-btn"
                      onClick={() => act("complete")}>Complete</Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {r.variants.map((v, i) => {
          const isWinner = data.winner_subject_id === v.subject_id;
          const rate = rateOf(v);
          const ci = ciOf(v);
          return (
            <Card key={v.subject_id}
                    className={isWinner ? "border-emerald-500" : ""}
                    data-testid={`variant-card-${v.subject_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  Variant {i === 0 ? "A (control)" : String.fromCharCode(65 + i)}
                  {isWinner && <Badge className="bg-emerald-600">Winner</Badge>}
                </CardTitle>
                <div className="text-xs text-muted-foreground font-mono break-all">
                  {v.subject_id}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Exposures</span>
                    <div className="font-medium" data-testid={`variant-exposures-${v.subject_id}`}>{v.exposures}</div></div>
                  <div><span className="text-muted-foreground">Conversions</span>
                    <div className="font-medium">{v.conversions}</div></div>
                  <div><span className="text-muted-foreground">Rate</span>
                    <div className="font-medium" data-testid={`variant-rate-${v.subject_id}`}>{pct(rate)}</div></div>
                  <div><span className="text-muted-foreground">95% CI</span>
                    <div className="font-medium">{pct(ci.low)} – {pct(ci.high)}</div></div>
                </div>
                {/* Bar visualisation against the highest-rate variant. */}
                <div className="h-2 bg-stone-100 rounded overflow-hidden">
                  <div className="h-2 bg-emerald-500"
                        style={{ width: pct(rate / Math.max(...r.variants.map(vv => rateOf(vv) || 0.001))) }} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Significance</CardTitle></CardHeader>
        <CardContent className="text-sm space-y-1">
          <div>p-value: <span className="font-mono" data-testid="experiment-p-value">{r.p_value == null ? "—" : r.p_value.toFixed(4)}</span></div>
          <div>z-score: <span className="font-mono">{r.z == null ? "—" : r.z.toFixed(3)}</span></div>
          <div>Status: {r.significant
            ? <Badge className="bg-emerald-600" data-testid="significance-yes">Significant ✓</Badge>
            : <Badge variant="outline" data-testid="significance-no">Not yet</Badge>}</div>
          <div>Min sample size met: {r.min_sample_size_met ? "yes" : "no"}</div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ExperimentCreate() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [subjectType, setSubjectType] = useState("landing_page");
  const [subjectIds, setSubjectIds] = useState("");
  const [splitText, setSplitText] = useState("0.5,0.5");
  const [primaryMetric, setPrimaryMetric] = useState("conversion_rate");
  const [busy, setBusy] = useState(false);

  const ids = useMemo(() => subjectIds.split(",").map(s => s.trim()).filter(Boolean), [subjectIds]);
  const split = useMemo(() => splitText.split(",").map(s => parseFloat(s.trim())).filter(n => !Number.isNaN(n)), [splitText]);
  const splitSum = split.reduce((a, b) => a + b, 0);
  const splitOk = Math.abs(splitSum - 1.0) < 0.01;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/experiments", {
        name, subject_type: subjectType,
        subject_ids: ids, traffic_split: split,
        primary_metric: primaryMetric,
      });
      toast.success("Experiment created");
      navigate(`/app/experiments/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="experiment-create-form">
      <h1 className="text-2xl font-semibold">New experiment</h1>
      <div className="space-y-3">
        <div>
          <Label>Name</Label>
          <Input value={name} onChange={e => setName(e.target.value)}
                  data-testid="exp-create-name" />
        </div>
        <div>
          <Label>Subject type</Label>
          <Select value={subjectType} onValueChange={setSubjectType}>
            <SelectTrigger data-testid="exp-create-subject-type"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="landing_page">Landing page</SelectItem>
              <SelectItem value="ad_creative">Ad creative</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Subject IDs (comma-separated, 2+)</Label>
          <Input value={subjectIds} onChange={e => setSubjectIds(e.target.value)}
                  data-testid="exp-create-subject-ids"
                  placeholder="lp-abc, lp-xyz" />
        </div>
        <div>
          <Label>Traffic split (must sum to 1.0)</Label>
          <Input value={splitText} onChange={e => setSplitText(e.target.value)}
                  data-testid="exp-create-split"
                  placeholder="0.5,0.5" />
          <p className={`text-xs mt-1 ${splitOk ? "text-emerald-600" : "text-rose-600"}`}
              data-testid="exp-create-split-sum">
            Sum: {splitSum.toFixed(2)}
          </p>
        </div>
        <div>
          <Label>Primary metric</Label>
          <Select value={primaryMetric} onValueChange={setPrimaryMetric}>
            <SelectTrigger data-testid="exp-create-metric"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="conversion_rate">Conversion rate</SelectItem>
              <SelectItem value="ctr">CTR</SelectItem>
              <SelectItem value="cpl">CPL</SelectItem>
              <SelectItem value="roas">ROAS</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button onClick={submit} disabled={busy || !name || ids.length < 2 || !splitOk || ids.length !== split.length}
                  data-testid="exp-create-submit">Create</Button>
          <Button variant="outline" onClick={() => navigate("/app/experiments")}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
